/**
 * Import Modal Component
 * Allows users to bulk import data from Excel or CSV files
 */

import React, { useState, useCallback } from 'react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Upload, Download, CheckCircle, XCircle, AlertCircle, FileSpreadsheet } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import {
  downloadTemplate,
  importUsers,
  importObjectives,
  importKeyResults,
  ImportType,
  ImportResult,
} from '../../services/import.service';
import { getErrorMessage } from '../../services/api';

interface ImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: ImportType;
  onImportComplete?: () => void;
}

export const ImportModal: React.FC<ImportModalProps> = ({ isOpen, onClose, type, onImportComplete }) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [dragActive, setDragActive] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  // Get import function based on type
  const getImportFunction = (importType: ImportType) => {
    switch (importType) {
      case 'users':
        return importUsers;
      case 'objectives':
        return importObjectives;
      case 'key-results':
        return importKeyResults;
    }
  };

  // Import mutation
  const importMutation = useMutation({
    mutationFn: (file: File) => getImportFunction(type)(file),
    onSuccess: (result) => {
      setImportResult(result);
      if (result.success) {
        toast.success(
          `Successfully imported ${result.successCount} ${getTypeLabel(type).toLowerCase()}`
        );
        if (onImportComplete) {
          onImportComplete();
        }
      } else {
        toast.error(
          `Import completed with ${result.errorCount} errors. Check the results below.`
        );
      }
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  // Download template mutation
  const downloadMutation = useMutation({
    mutationFn: ({ type, format }: { type: ImportType; format: 'xlsx' | 'csv' }) =>
      downloadTemplate(type, format),
    onSuccess: () => {
      toast.success('Template downloaded successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));
    },
  });

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      if (isValidFile(file)) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        toast.error('Invalid file type. Please upload an Excel (.xlsx) or CSV (.csv) file.');
      }
    }
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (isValidFile(file)) {
        setSelectedFile(file);
        setImportResult(null);
      } else {
        toast.error('Invalid file type. Please upload an Excel (.xlsx) or CSV (.csv) file.');
      }
    }
  };

  const isValidFile = (file: File): boolean => {
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
      'text/csv',
    ];
    return validTypes.includes(file.type) || file.name.endsWith('.xlsx') || file.name.endsWith('.csv');
  };

  const handleImport = () => {
    if (selectedFile) {
      importMutation.mutate(selectedFile);
    }
  };

  const handleDownloadTemplate = (format: 'xlsx' | 'csv') => {
    downloadMutation.mutate({ type, format });
  };

  const handleClose = () => {
    setSelectedFile(null);
    setImportResult(null);
    onClose();
  };

  const getTypeLabel = (importType: ImportType): string => {
    switch (importType) {
      case 'users':
        return 'Users';
      case 'objectives':
        return 'Objectives';
      case 'key-results':
        return 'Key Results';
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Import ${getTypeLabel(type)}`} size="lg">
      <div className="space-y-6">
        {/* Instructions */}
        <div className="rounded-lg bg-blue-50 p-4">
          <div className="flex items-start">
            <AlertCircle className="mr-2 mt-0.5 h-5 w-5 text-blue-600" />
            <div className="text-sm text-blue-800">
              <p className="font-medium">How to import:</p>
              <ol className="ml-4 mt-2 list-decimal space-y-1">
                <li>Download the template file (Excel or CSV)</li>
                <li>Fill in your data following the template structure</li>
                <li>Upload the completed file</li>
                <li>Review the validation results</li>
                <li>Confirm the import</li>
              </ol>
            </div>
          </div>
        </div>

        {/* Download Template Section */}
        <div>
          <h4 className="mb-3 text-sm font-medium text-gray-900">Step 1: Download Template</h4>
          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => handleDownloadTemplate('xlsx')}
              disabled={downloadMutation.isPending}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Excel Template
            </Button>
            <Button
              variant="outline"
              onClick={() => handleDownloadTemplate('csv')}
              disabled={downloadMutation.isPending}
            >
              <Download className="mr-2 h-4 w-4" />
              Download CSV Template
            </Button>
          </div>
        </div>

        {/* Upload Section */}
        <div>
          <h4 className="mb-3 text-sm font-medium text-gray-900">Step 2: Upload Completed File</h4>
          <div
            className={`relative rounded-lg border-2 border-dashed p-8 text-center transition-colors ${
              dragActive
                ? 'border-primary-500 bg-primary-50'
                : 'border-gray-300 hover:border-gray-400'
            }`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            <input
              type="file"
              id="file-upload"
              className="hidden"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              disabled={importMutation.isPending}
            />
            <label htmlFor="file-upload" className="cursor-pointer">
              {selectedFile ? (
                <div className="flex flex-col items-center">
                  <FileSpreadsheet className="mb-2 h-12 w-12 text-green-600" />
                  <p className="text-sm font-medium text-gray-900">{selectedFile.name}</p>
                  <p className="text-xs text-gray-500">{formatFileSize(selectedFile.size)}</p>
                  <Button variant="outline" size="sm" className="mt-4">
                    Choose Different File
                  </Button>
                </div>
              ) : (
                <div className="flex flex-col items-center">
                  <Upload className="mb-2 h-12 w-12 text-gray-400" />
                  <p className="text-sm font-medium text-gray-900">
                    Drop your file here, or click to browse
                  </p>
                  <p className="mt-1 text-xs text-gray-500">Excel (.xlsx) or CSV (.csv) files only</p>
                  <p className="mt-1 text-xs text-gray-500">Maximum file size: 5MB</p>
                </div>
              )}
            </label>
          </div>
        </div>

        {/* Import Results */}
        {importResult && (
          <div className="rounded-lg border border-gray-200 p-4">
            <h4 className="mb-3 text-sm font-medium text-gray-900">Import Results</h4>
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-gray-600">Total Rows:</span>
                <span className="font-medium text-gray-900">{importResult.totalRows}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="flex items-center text-sm text-green-600">
                  <CheckCircle className="mr-1 h-4 w-4" />
                  Successful:
                </span>
                <span className="font-medium text-green-600">{importResult.successCount}</span>
              </div>
              {importResult.errorCount > 0 && (
                <div className="flex items-center justify-between">
                  <span className="flex items-center text-sm text-red-600">
                    <XCircle className="mr-1 h-4 w-4" />
                    Failed:
                  </span>
                  <span className="font-medium text-red-600">{importResult.errorCount}</span>
                </div>
              )}
            </div>

            {/* Error Details */}
            {importResult.errors.length > 0 && (
              <div className="mt-4 max-h-48 overflow-y-auto rounded border border-red-200 bg-red-50 p-3">
                <h5 className="mb-2 text-xs font-medium text-red-900">Error Details:</h5>
                <div className="space-y-1">
                  {importResult.errors.map((error, index) => (
                    <div key={index} className="text-xs text-red-800">
                      <span className="font-medium">Row {error.row}:</span>{' '}
                      {error.field && <span className="italic">({error.field})</span>} {error.message}
                      {error.value && <span className="ml-1 text-red-600">- {error.value}</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex justify-end gap-3 border-t border-gray-200 pt-4">
          <Button variant="outline" onClick={handleClose} disabled={importMutation.isPending}>
            {importResult ? 'Close' : 'Cancel'}
          </Button>
          {!importResult && (
            <Button
              onClick={handleImport}
              disabled={!selectedFile || importMutation.isPending}
              isLoading={importMutation.isPending}
            >
              <Upload className="mr-2 h-4 w-4" />
              Import Data
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
};
